import * as React from 'react';
import { Button } from '../../actions';
import { Icon } from '../../display';
import { Select } from '../../inputs';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Type for page number entries including ellipsis markers
 */
type PageNumberEntry = number | 'ellipsis-start' | 'ellipsis-end';

export interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * Current page (1-indexed)
   */
  page: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Total number of items (for "Showing X of Y" text)
   */
  totalItems?: number;
  /**
   * Items per page
   */
  pageSize?: number;
  /**
   * Available page size options for the selector
   */
  pageSizeOptions?: number[];
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Callback when page size changes
   */
  onPageSizeChange?: (pageSize: number) => void;
  /**
   * Number of pages to show around the current page
   * @default 1
   */
  siblingCount?: number;
  /**
   * Whether to show first/last page buttons
   * @default false
   */
  showFirstLast?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Generate page numbers to display with ellipsis
 */
function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  siblingCount: number
): PageNumberEntry[] {
  // Calculate the range of pages to show
  const totalPageNumbers = siblingCount * 2 + 3; // siblings on each side + first + last + current
  const totalBlocks = totalPageNumbers + 2; // + 2 for ellipsis

  // If total pages is less than what we'd show, show all
  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    // Show pages from start
    const leftRange = 1 + 2 * siblingCount + 2;
    const pages: PageNumberEntry[] = Array.from(
      { length: leftRange },
      (_, i) => i + 1
    );
    pages.push('ellipsis-end', totalPages);
    return pages;
  }

  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    // Show pages at end
    const rightRange = 1 + 2 * siblingCount + 2;
    const pages: PageNumberEntry[] = [1, 'ellipsis-start'];
    const startPage = totalPages - rightRange + 1;
    for (let i = startPage; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Show ellipsis on both sides
  const pages: PageNumberEntry[] = [1, 'ellipsis-start'];
  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    pages.push(i);
  }
  pages.push('ellipsis-end', totalPages);
  return pages;
}

/**
 * Format number with thousands separator
 */
function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Pagination component for navigating through paged data.
 *
 * Features:
 * - Previous/next navigation buttons
 * - First/last page buttons (optional)
 * - Page number buttons with ellipsis for large page counts
 * - "Showing X-Y of Z items" text
 * - Page size selector
 * - Keyboard accessible
 * - Follows WAI-ARIA practices
 *
 * @example
 * // Basic pagination
 * <Pagination
 *   page={1}
 *   totalPages={10}
 *   onPageChange={(page) => setPage(page)}
 * />
 *
 * @example
 * // With items info and page size selector
 * <Pagination
 *   page={1}
 *   totalPages={50}
 *   totalItems={1247}
 *   pageSize={25}
 *   pageSizeOptions={[10, 25, 50, 100]}
 *   onPageChange={(page) => setPage(page)}
 *   onPageSizeChange={(size) => setPageSize(size)}
 * />
 */
const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  (
    {
      page,
      totalPages,
      totalItems,
      pageSize = 25,
      pageSizeOptions,
      onPageChange,
      onPageSizeChange,
      siblingCount = 1,
      showFirstLast = false,
      className = '',
      ...props
    },
    ref
  ) => {
    // Ensure page is within bounds
    const currentPage = Math.max(1, Math.min(page, totalPages));

    // Calculate showing range
    const startItem = totalItems ? (currentPage - 1) * pageSize + 1 : 0;
    const endItem = totalItems ? Math.min(currentPage * pageSize, totalItems) : 0;

    // Generate page numbers to display
    const pageNumbers = generatePageNumbers(currentPage, totalPages, siblingCount);

    // Navigation handlers
    const goToPage = (newPage: number) => {
      /* v8 ignore start */
      if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
        /* v8 ignore stop */
        onPageChange(newPage);
      }
    };

    const goToFirstPage = () => goToPage(1);
    const goToLastPage = () => goToPage(totalPages);
    const goToPreviousPage = () => goToPage(currentPage - 1);
    const goToNextPage = () => goToPage(currentPage + 1);

    // Page size selector options
    const pageSizeSelectOptions = pageSizeOptions?.map((size) => ({
      value: String(size),
      label: `${size} per page`,
    }));

    // Handle page size change
    const handlePageSizeChange = (value: string | string[]) => {
      /* v8 ignore start */
      const newSize = Number(Array.isArray(value) ? value[0] : value);
      if (onPageSizeChange && !Number.isNaN(newSize)) {
        /* v8 ignore stop */
        onPageSizeChange(newSize);
      }
    };

    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;

    // Base classes for page buttons
    const pageButtonBase =
      'inline-flex items-center justify-center h-8 min-w-[32px] px-2 text-sm font-medium rounded-md transition-all duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-offset-2';
    const pageButtonInactive =
      'text-secondary-700 hover:bg-secondary-100 active:bg-secondary-200';
    const pageButtonActive =
      'bg-primary-700 text-white hover:bg-primary-800';

    const hasLeftContent = totalItems !== undefined || (pageSizeSelectOptions && onPageSizeChange);

    const combinedClasses = [
      'flex items-center gap-4 flex-wrap',
      hasLeftContent ? 'justify-between' : 'justify-end',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <nav
        ref={ref}
        aria-label="Pagination"
        className={combinedClasses}
        {...props}
      >
        {/* Left side: Showing X-Y of Z */}
        {hasLeftContent && (
          <div className="flex items-center gap-4">
            {totalItems !== undefined && (
              <p className="text-sm text-secondary-500">
                Showing{' '}
                <span className="font-medium text-secondary-800">
                  {formatNumber(startItem)}-{formatNumber(endItem)}
                </span>{' '}
                of{' '}
                <span className="font-medium text-secondary-800">
                  {formatNumber(totalItems)}
                </span>
              </p>
            )}

            {/* Page size selector */}
            {pageSizeSelectOptions && onPageSizeChange && (
              <div className="flex items-center gap-2">
                <Select
                  options={pageSizeSelectOptions}
                  value={String(pageSize)}
                  onChange={handlePageSizeChange}
                  size="sm"
                  className="w-[140px]"
                  aria-label="Items per page"
                />
              </div>
            )}
          </div>
        )}

        {/* Right side: Page navigation */}
        <div className="flex items-center gap-1">
          {/* First page button */}
          {showFirstLast && (
            <Button
              variant="ghost"
              size="sm"
              onClick={goToFirstPage}
              disabled={isFirstPage}
              aria-label="Go to first page"
              className="h-8 w-8 p-0"
            >
              <Icon icon={ChevronsLeft} size="sm" />
            </Button>
          )}

          {/* Previous page button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPreviousPage}
            disabled={isFirstPage}
            aria-label="Go to previous page"
            className="h-8 w-8 p-0"
          >
            <Icon icon={ChevronLeft} size="sm" />
          </Button>

          {/* Page numbers */}
          <fieldset className="flex items-center gap-1 border-0 p-0 m-0 min-w-0" aria-label="Page numbers">
            {pageNumbers.map((pageNum, index) => {
              if (pageNum === 'ellipsis-start' || pageNum === 'ellipsis-end') {
                return (
                  <span
                    key={`${pageNum}-${index}`}
                    className="inline-flex items-center justify-center h-8 min-w-[32px] px-1 text-sm text-secondary-500"
                    aria-hidden="true"
                  >
                    ...
                  </span>
                );
              }

              const isCurrentPage = pageNum === currentPage;
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => goToPage(pageNum)}
                  disabled={isCurrentPage}
                  aria-label={`Page ${pageNum}`}
                  aria-current={isCurrentPage ? 'page' : undefined}
                  className={[
                    pageButtonBase,
                    isCurrentPage ? pageButtonActive : pageButtonInactive,
                  ].join(' ')}
                >
                  {pageNum}
                </button>
              );
            })}
          </fieldset>

          {/* Next page button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextPage}
            disabled={isLastPage}
            aria-label="Go to next page"
            className="h-8 w-8 p-0"
          >
            <Icon icon={ChevronRight} size="sm" />
          </Button>

          {/* Last page button */}
          {showFirstLast && (
            <Button
              variant="ghost"
              size="sm"
              onClick={goToLastPage}
              disabled={isLastPage}
              aria-label="Go to last page"
              className="h-8 w-8 p-0"
            >
              <Icon icon={ChevronsRight} size="sm" />
            </Button>
          )}
        </div>
      </nav>
    );
  }
);

Pagination.displayName = 'Pagination';

export { Pagination };
